// Analytics module — top-level page with 9 tabs and global filters.
import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LineChart, Layers, BarChart3, ShoppingBag, Megaphone, Truck, Users, Banknote, Sparkles, FileText, Bell, RefreshCw,
} from "lucide-react";
import DateRangeFilter from "./components/DateRangeFilter";
import SettingsDialog from "./components/SettingsDialog";
import AdSpendDialog from "./components/AdSpendDialog";
import OverviewTab from "./tabs/OverviewTab";
import SalesTab from "./tabs/SalesTab";
import ProductsTab from "./tabs/ProductsTab";
import MarketingTab from "./tabs/MarketingTab";
import DeliveryTab from "./tabs/DeliveryTab";
import CustomersTab from "./tabs/CustomersTab";
import ProfitTab from "./tabs/ProfitTab";
import ForecastingTab from "./tabs/ForecastingTab";
import ReportsTab from "./tabs/ReportsTab";
import { Button } from "@/components/ui/button";
import { getAlerts } from "@/lib/analytics";

const TABS = [
  { id: "overview", label: "Overview", icon: Layers, Component: OverviewTab },
  { id: "sales", label: "Sales", icon: BarChart3, Component: SalesTab },
  { id: "products", label: "Produse", icon: ShoppingBag, Component: ProductsTab },
  { id: "marketing", label: "Marketing", icon: Megaphone, Component: MarketingTab },
  { id: "delivery", label: "Livrări", icon: Truck, Component: DeliveryTab },
  { id: "customers", label: "Clienți", icon: Users, Component: CustomersTab },
  { id: "profit", label: "Profit", icon: Banknote, Component: ProfitTab },
  { id: "forecasting", label: "Forecasting", icon: Sparkles, Component: ForecastingTab },
  { id: "reports", label: "Rapoarte", icon: FileText, Component: ReportsTab },
];

export default function AnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabId = searchParams.get("tab") || "overview";
  const rangeId = searchParams.get("range") || "30d";
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const range = useMemo(() => (from && to ? { from, to } : { range: rangeId }), [rangeId, from, to]);
  const [alerts, setAlerts] = useState({ alerts: [] });
  const [refreshTick, setRefreshTick] = useState(0);

  const setTab = (id) => {
    setSearchParams((p) => { const next = new URLSearchParams(p); next.set("tab", id); return next; });
  };
  const setRange = (r) => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      if (r.from) { next.set("from", r.from); next.set("to", r.to); next.delete("range"); }
      else { next.set("range", r.range); next.delete("from"); next.delete("to"); }
      return next;
    });
  };

  useEffect(() => {
    getAlerts().then(setAlerts).catch(() => {});
    const t = setInterval(() => getAlerts().then(setAlerts).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, []);

  const Tab = TABS.find((t) => t.id === tabId) || TABS[0];
  const Active = Tab.Component;
  const alertCount = alerts.alerts?.length || 0;
  const dangerCount = alerts.alerts?.filter((a) => a.level === "danger").length || 0;

  return (
    <div
      className="min-h-[calc(100vh-7rem)] -m-4 sm:-m-6 lg:-m-8 flex flex-col bg-background border-t border-border"
      data-testid="analytics-page"
    >
      {/* HEADER */}
      <div className="border-b border-border bg-background/60 backdrop-blur sticky top-0 z-20">
        <div className="px-6 py-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 mr-3">
            <LineChart className="h-5 w-5 text-primary" />
            <div>
              <div className="font-display font-semibold text-lg leading-tight">Analytics</div>
              <div className="text-[11px] text-muted-foreground">
                Executive dashboard · profit real, livrare, forecasting
              </div>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-end gap-2 flex-wrap">
            <DateRangeFilter range={range} onChange={setRange} />
            <Button
              variant="ghost"
              size="sm"
              className="h-9 relative"
              onClick={() => { getAlerts().then(setAlerts); setRefreshTick((t) => t + 1); }}
              data-testid="analytics-refresh"
              title="Reîmprospătează"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 relative"
              onClick={() => setTab("forecasting")}
              data-testid="analytics-alerts-btn"
              title={`${alertCount} alerte`}
            >
              <Bell className="h-3.5 w-3.5" />
              {alertCount > 0 && (
                <span className={`absolute -top-0.5 -right-0.5 h-4 min-w-4 rounded-full text-[9px] flex items-center justify-center px-1 ${dangerCount > 0 ? "bg-rose-500" : "bg-amber-500"} text-white font-bold`}>
                  {alertCount}
                </span>
              )}
            </Button>
            <SettingsDialog />
            <AdSpendDialog range={range} trigger={
              <Button variant="outline" size="sm" className="h-9" data-testid="header-adspend">
                <Megaphone className="h-3.5 w-3.5 mr-1.5" /> Ads
              </Button>
            } />
          </div>
        </div>

        {/* TAB BAR */}
        <div className="px-6 pb-3 flex flex-wrap gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.id === tabId;
            return (
              <button
                key={t.id}
                data-testid={`analytics-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`text-[12px] px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                }`}
              >
                <Icon className="h-3 w-3" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 p-6">
        <Active key={`${tabId}-${refreshTick}`} range={range} />
      </div>
    </div>
  );
}
